import { Component, OnInit, Renderer2 } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { BaseService } from '../../services/base.service';
import {
  FORGOT_PASSWORD,
  LOGIN,
  RECAPTCHA_TOKEN,
  RESEND_VERIFY,
} from 'src/app/constants/endpoint.const';
import { takeUntil, Subject } from 'rxjs';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalService } from 'ng-zorro-antd/modal';
import { ModalRegisterSuccessComponent } from '../register/modal-register-success/modal-register-success.component';
import { setRegex } from 'src/app/libs/functions/common.functions';

@Component({
  standalone: false,
  selector: 'app-forgot-password',
  templateUrl: './forgot-password.component.html',
  styleUrls: ['./forgot-password.component.scss'],
})
export class ForgotPasswordComponent implements OnInit {
  setRegex = setRegex;
  formGroup: FormGroup = new FormGroup({
    email: new FormControl('', [Validators.required, Validators.email]),
  });

  isLoading: boolean = false;
  destroy$ = new Subject();
  errorUser = '';
  validEmail: boolean = true;
  success: boolean = false;

  constructor(
    private baseService: BaseService,
    private renderer: Renderer2,
    private message: NzMessageService,
    private BaseService: BaseService,
    private modalService: NzModalService
  ) {}
  ngOnInit(): void {
    this.addScriptToHead(
      `https://www.google.com/recaptcha/enterprise.js?render=${RECAPTCHA_TOKEN}`
    );
  }

  addScriptToHead(src: string): void {
    const script = this.renderer.createElement('script');
    script.src = src;
    script.type = 'text/javascript';
    script.async = true;
    this.renderer.appendChild(document.head, script);
  }

  disableEnterKey(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      event.preventDefault();
    }
  }

  validateEmail() {
    const value = this.formGroup.get('email')?.value || '';
    const regex = /^[a-zA-Z0-9+@._-]+$/g;

    if (value === '') {
      this.validEmail = false;
      this.errorUser = 'Email must be filled in';
    } else {
      if (value.length < 10) {
        this.errorUser = 'Minimum 10 characters';
        this.validEmail = false;
      } else if (value.length > 50) {
        this.errorUser = 'Maximum 50 characters';
        this.validEmail = false;
      } else if (!regex.test(value)) {
        this.validEmail = false;
        this.errorUser = 'Email must be a valid email address';
      } else {
        const atIndex = value.indexOf('@');
        const lastDotIndex = value.lastIndexOf('.');
        const dotBeforeAt = value.substring(0, atIndex).includes('.');
        const dotAfterAt = value.substring(atIndex + 1).includes('.');

        if (
          atIndex === -1 ||
          lastDotIndex === -1 ||
          lastDotIndex === value.length - 1 ||
          value[atIndex + 1] === '.' ||
          !dotAfterAt
        ) {
          this.errorUser = 'Email must be a valid email address';
          this.validEmail = false;
        } else {
          this.validEmail = true;
          this.errorUser = '';
        }
      }
    }
  }

  get form() {
    return {
      formGroup: this.formGroup,
      email: this.formGroup.get('email'),
    };
  }

  private recaptchaErrorCount = 0;

  async getCaptchaToken(event: Event) {
    event.preventDefault();

    grecaptcha.enterprise.ready(async () => {
      try {
        const token = await grecaptcha.enterprise.execute(
          `${RECAPTCHA_TOKEN}`,
          { action: 'FORGOT_PASSWORD' }
        );
        this.handleForgotPass(token, event);
      } catch (error) {
        console.error('Error executing reCAPTCHA:', error);
        this.message.create('error', 'Error executing reCAPTCHA');
      }
    });
  }

  handleForgotPass(e: string, event: Event) {
    const params = {
      email: this.formGroup.get('email')?.value,
      recaptcha_token: e,
    };
    this.baseService
      .postData(FORGOT_PASSWORD, params)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (response: any) => {
          this.isLoading = false;
          this.success = true;
        },
        (err) => {
          this.isLoading = false;
          if (
            err.error.meta.code === 403 ||
            err.error.meta.message === 'recaptcha failed'
          ) {
            this.recaptchaErrorCount++;
            if (this.recaptchaErrorCount > 4) {
              this.message.create(
                'error',
                'Too many failed reCAPTCHA attempts. Please try again later.'
              );
            } else {
              this.getCaptchaToken(event);
            }
          } else if (err.error.data.errors === 'user not activated') {
            this.errorUser = 'Your email has not been verified';
            this.validEmail = false;
          } else if (err.error.data.errors === 'record not found') {
            this.errorUser = 'Email not registered yet';
            this.validEmail = false;
          } else {
            this.message.create(
              'error',
              'Password Reset Failed, Please Try Again'
            );
          }
        }
      );
  }

  resendVerif(event: Event) {
    event.preventDefault();

    grecaptcha.enterprise.ready(async () => {
      try {
        const token = await grecaptcha.enterprise.execute(
          `${RECAPTCHA_TOKEN}`,
          { action: 'RESEND_ACTIVATION' }
        );

        const params = {
          email: this.formGroup.get('email')?.value,
          recaptcha_token: token,
        };

        this.BaseService.postData(RESEND_VERIFY, params)
          .pipe(takeUntil(this.destroy$))
          .subscribe(
            (response) => {
              this.modalService.create({
                nzContent: ModalRegisterSuccessComponent,
                nzFooter: null,
              });
            },
            (err) => {
              this.message.create(
                'error',
                'An error occurred. Please try again later.'
              );
            }
          );
      } catch (error) {
        console.error('Error executing reCAPTCHA:', error);
        this.message.create('error', 'Error executing reCAPTCHA');
      }
    });
  }

  ngOnDestroy() {}
}
